import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchGraphql } from '../graphql/fetch';

export type QueryStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseQueryArgs {
  query: string;
  variables?: Record<string, unknown>;
  // When true, the query is not run until `refetch()` is called.
  skip?: boolean;
}

export interface UseQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  // Force a network fetch (bypasses the per-variable cache) and resolve with the data.
  refetch: () => Promise<T | null>;
}

// Generic read hook for Premium Components that need to re-query the server on
// interaction (leaderboard phase switch, submissions pagination, insights round
// toggle) — things the single server-rendered `data` prop can't do.
//
// Contract (matches the design kit's preview stub):
//   - First render runs the query (unless `skip`).
//   - Re-runs whenever `query`/`variables` change.
//   - **Last-good data is retained** while a new fetch is in flight (no flash to null).
//   - **Per-variable cache**: switching back to a previously-fetched variable set
//     resolves synchronously from cache (no spinner, no network).
//   - **`refetch()` bypasses the cache** (force-refresh the current variables).
export function useQuery<T = unknown>({ query, variables, skip = false }: UseQueryArgs): UseQueryResult<T> {
  const cache = useRef<Map<string, T>>(new Map());
  const mounted = useRef(true);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => () => { mounted.current = false; }, []);

  const varsKey = JSON.stringify(variables ?? null);

  const fetchNow = useCallback(
    async (bypassCache: boolean): Promise<T | null> => {
      if (!bypassCache && cache.current.has(varsKey)) {
        const cached = cache.current.get(varsKey) as T;
        if (mounted.current) {
          setData(cached);
          setError(null);
          setLoading(false);
        }
        return cached;
      }

      if (mounted.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const res = await fetchGraphql<T>({ query, variables });
        if (res.errors && res.errors.length) {
          throw new Error(res.errors[0].message || 'GraphQL error');
        }
        const next = (res.data ?? null) as T | null;
        if (next !== null) cache.current.set(varsKey, next);
        if (mounted.current) setData(next);
        return next;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        if (mounted.current) setError(err);
        throw err;
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    // `varsKey` is a stable serialization of `variables`; `query` is included directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, varsKey]
  );

  useEffect(() => {
    if (skip) return;
    fetchNow(false).catch(() => {
      /* surfaced via `error` */
    });
  }, [fetchNow, skip]);

  const refetch = useCallback(() => fetchNow(true), [fetchNow]);

  return { data, loading, error, refetch };
}
